import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { useChurchPlanner, usePlannerRoles } from '../../hooks/useChurchPlanner';
import {
  PLANNER_TEAM_DEFINITIONS,
  resolveTeamIdFromRole,
} from '../../hooks/usePlannerInsights';
import {
  PlannerPage,
  PlannerCard,
  PlannerKicker,
  PlannerHeading,
  PlannerSubtle,
  PlannerError,
  plannerColors,
} from './plannerTheme';
import { normalizePlannerStatus } from './plannerStatus';

const SummaryCard = styled(PlannerCard)`
  display: grid;
  gap: 0.55rem;
`;

const Score = styled.span`
  font-size: 2.2rem;
  line-height: 1;
  font-weight: 800;
  color: ${plannerColors.accent};
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.7rem;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const MetricCard = styled.div`
  border: 1px solid ${plannerColors.border};
  border-radius: 12px;
  padding: 0.75rem 0.8rem;
  background: ${plannerColors.surfaceHover};
`;

const MetricRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.65rem;
`;

const MetricLabel = styled.span`
  font-size: 0.82rem;
  color: ${plannerColors.text};
  font-weight: 600;
`;

const MetricValue = styled.span`
  font-size: 0.82rem;
  color: ${plannerColors.accent};
  font-weight: 700;
`;

const MetricMeta = styled.span`
  margin-top: 0.38rem;
  display: inline-block;
  font-size: 0.73rem;
  color: ${plannerColors.muted};
`;

const Track = styled.div`
  margin-top: 0.5rem;
  height: 8px;
  border-radius: 999px;
  background: ${plannerColors.border};
  overflow: hidden;
`;

const Fill = styled.div<{ $value: number }>`
  height: 100%;
  border-radius: 999px;
  width: ${({ $value }) => `${$value}%`};
  background: ${plannerColors.accent};
`;

function firstRelation<T>(value: T | T[] | null | undefined): T | null {
  if (!value) return null;
  return Array.isArray(value) ? value[0] ?? null : value;
}

export default function ChurchPlannerServiceHealthPage() {
  const { user } = useAuth();
  const planner = useChurchPlanner(true, user?.id);
  const rolesQuery = usePlannerRoles(true);

  const sourcePlans = planner.upcomingPlans.length > 0 ? planner.upcomingPlans : planner.plans;

  const roleById = useMemo(() => {
    const map = new Map<string, { id: string; name: string; area?: string | null }>();
    (rolesQuery.data ?? []).forEach((role) => {
      map.set(role.id, role);
    });
    return map;
  }, [rolesQuery.data]);

  const requiredRowsByTeam = useMemo(() => {
    const byTeam = new Map<string, Array<{ id: string; team_id: string; role_id: string }>>();
    (planner.teamRequiredRolesQuery.data ?? []).forEach((row) => {
      const entries = byTeam.get(row.team_id) ?? [];
      entries.push(row);
      byTeam.set(row.team_id, entries);
    });
    return byTeam;
  }, [planner.teamRequiredRolesQuery.data]);

  const filledRoleIdsByTeam = useMemo(() => {
    const map = new Map<string, Set<string>>();

    sourcePlans.forEach((plan) => {
      (plan.service_plan_assignments ?? []).forEach((assignment) => {
        if (!assignment.user_id) return;
        const normalizedStatus = normalizePlannerStatus(assignment.status);
        if (normalizedStatus === 'unavailable') return;
        const role = firstRelation(assignment.service_plan_roles);
        const roleId = assignment.role_id || role?.id;
        if (!roleId) return;

        const resolvedRole = roleById.get(roleId) ?? role;
        const teamId = resolveTeamIdFromRole(resolvedRole);
        if (!teamId) return;

        const filled = map.get(teamId) ?? new Set<string>();
        filled.add(roleId);
        map.set(teamId, filled);
      });
    });

    return map;
  }, [roleById, sourcePlans]);

  const metrics = useMemo(
    () =>
      PLANNER_TEAM_DEFINITIONS.map((team) => {
        const requiredRows = requiredRowsByTeam.get(team.id) ?? [];
        const requiredRoleIds = Array.from(new Set(requiredRows.map((row) => row.role_id)));
        const filledSet = filledRoleIdsByTeam.get(team.id) ?? new Set<string>();
        const fulfilledRequired = requiredRoleIds.filter((roleId) => filledSet.has(roleId)).length;
        const totalRequired = requiredRoleIds.length;

        return {
          roleId: team.id,
          label: team.label,
          totalAssignments: totalRequired,
          filledAssignments: fulfilledRequired,
          coverage: totalRequired > 0 ? Math.round((fulfilledRequired / totalRequired) * 100) : 0,
          hasRequiredRoles: totalRequired > 0,
        };
      }),
    [filledRoleIdsByTeam, requiredRowsByTeam]
  );
  const overallCoverage = useMemo(() => {
    const totals = metrics.reduce(
      (acc, metric) => {
        acc.total += metric.totalAssignments;
        acc.filled += metric.filledAssignments;
        return acc;
      },
      { total: 0, filled: 0 }
    );

    if (!totals.total) return 0;
    return Math.round((totals.filled / totals.total) * 100);
  }, [metrics]);
  const hasRequiredRoles = metrics.some((metric) => metric.hasRequiredRoles);

  if (planner.isLoading || rolesQuery.isLoading) {
    return (
      <PlannerPage>
        <PlannerCard>
          <PlannerSubtle>Loading service health…</PlannerSubtle>
        </PlannerCard>
      </PlannerPage>
    );
  }

  if (planner.isError || rolesQuery.isError) {
    return (
      <PlannerPage>
        <PlannerCard>
          <PlannerError>Unable to load service health right now.</PlannerError>
        </PlannerCard>
      </PlannerPage>
    );
  }

  return (
    <PlannerPage>
      <SummaryCard>
        <PlannerKicker>Insights</PlannerKicker>
        <PlannerHeading>Service health</PlannerHeading>
        <Score>{overallCoverage}%</Score>
        <PlannerSubtle>
          Coverage measures required roles filled by assigned members who are not declined/unavailable.
        </PlannerSubtle>
      </SummaryCard>

      <PlannerCard>
        <MetricsGrid>
          {metrics.map((metric) => (
            <MetricCard key={metric.roleId}>
              <MetricRow>
                <MetricLabel>{metric.label}</MetricLabel>
                <MetricValue>{metric.hasRequiredRoles ? `${metric.coverage}%` : 'N/A'}</MetricValue>
              </MetricRow>
              <Track>
                <Fill $value={metric.coverage} />
              </Track>
              <MetricMeta>
                {metric.hasRequiredRoles
                  ? `${metric.filledAssignments} of ${metric.totalAssignments} required roles filled`
                  : 'No required roles configured'}
              </MetricMeta>
            </MetricCard>
          ))}
        </MetricsGrid>
        {!hasRequiredRoles ? (
          <PlannerSubtle style={{ marginTop: '0.75rem' }}>
            Team health will update when required roles are configured for each team.
          </PlannerSubtle>
        ) : null}
      </PlannerCard>
    </PlannerPage>
  );
}
